import { captureMessage, withScope } from "@sentry/react";
import { useContext } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAlert } from "@/hooks/useAlert";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import { DataContext } from "@/provider/DataContextProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  useAddOption,
  useCompleteMnpData,
  useSetMnpData,
} from "@/services/api";
import { CompleteMnpData, MnpData } from "@/services/model";
import { AlertTypes } from "@/utils/atoms";
import { log } from "@/utils/log";

import { NumberPortingFormProps } from "./NumberPorting";
import { mnpContractType, mnpTime } from "./NumberPorting.utils";
import { MSISDN_VALIDATION_STATUS } from "./useMsisdnValidation";

/**
 * Hook providing actions for submitting Number Porting data.
 * Supports both the Onboarding flow (setting data in basket) and the Portal flow (completing existing data).
 *
 * @param methods - React Hook Form return object.
 * @param providers - Map of provider names to IDs.
 * @param wishdateSelectable - Flag indicating if a custom wish date can be selected.
 * @returns Object with submission functions and loading states.
 */
export const useNumberPortingActions = (
  methods: UseFormReturn<NumberPortingFormProps>,
  providers: Record<string, number> | undefined,
  wishdateSelectable: boolean,
) => {
  const { dataContext, setDataContext } = useContext(DataContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showAlert = useAlert();
  const [simId] = useSimIdFromQueryParam();
  const { setError } = methods;

  const navigateToNextStep = () => {
    navigate(portalRoutes.addDevice.checkout);
  };

  const showGenericError = () => {
    showAlert({
      text: t("Common.generalForm.error.genericError"),
      type: AlertTypes.error,
    });
  };

  /**
   * Mutation for setting MNP data in the onboarding basket.
   */
  const { mutate: setMnpData, isLoading: isSettingMnpData } = useSetMnpData({
    mutation: {
      onError: (error: any) => {
        // Handle specific backend validation errors.
        if (`${error?.response?.data}`.includes("invalid phone number")) {
          setError("currentNumber", {
            type: "backendError",
            message: t("translation:label.validation.phoneBackend"),
          });
        } else {
          showGenericError();
        }
      },
    },
  });

  /**
   * Clears ONP on the basket when the user explicitly chooses not to port.
   * Rate selection / sim verification set isONP=true for Flat Mobile; without
   * this call checkout fails because the backend still expects MNP data.
   */
  const { mutate: addOption, isLoading: isClearingOnp } = useAddOption({
    mutation: {
      onError: showGenericError,
    },
  });

  /**
   * Mutation for completing MNP data for an existing SIM (Portal flow).
   */
  const { mutate: completeMnpData, isLoading: isCompletingMnpData } =
    useCompleteMnpData({
      mutation: {
        onSuccess: () => {
          showAlert({
            text: t("Onboarding.sections.number-porting.completed"),
            type: AlertTypes.success,
          });
          navigate(portalRoutes.cockpit.base, { replace: true });
        },
        onError: () => {
          showAlert({
            text: t("translation:Common.generalForm.error.genericError"),
            type: AlertTypes.error,
          });
        },
      },
    });

  /**
   * Main submit handler for the Onboarding form.
   *
   * @param data - Form data from React Hook Form.
   */
  const onSubmit = (data: NumberPortingFormProps) => {
    // Persist ONP=NO on the basket — local patch alone leaves entry.onp true.
    if (data.enableNumberPorting === "no") {
      const entry = dataContext.basket?.entries?.["0"];
      const basketId = dataContext.basket?.id;
      const uuid = entry?.uuid;
      const optionId =
        dataContext.selectedOption?.id ?? entry?.options?.[0]?.product?.id;

      if (!basketId || !uuid || !optionId) {
        showGenericError();

        return;
      }

      addOption(
        {
          basketId,
          uuid,
          params: {
            optionId,
            quantity: 1,
            isONP: false,
          },
        },
        {
          onSuccess: (basket) => {
            setDataContext((prev) => ({
              ...prev,
              basket,
              numberPorting: data,
              allowedMaxOnboardingStep: 4,
            }));
            navigateToNextStep();
          },
        },
      );

      return;
    }

    const providerId = providers?.[data.currentProvider];
    if (!providerId) {
      setError("currentProvider", {
        type: "backendError",
        message: t("Common.generalForm.error.genericError"),
      });

      return;
    }

    // Proceed if basket is available.
    if (dataContext.basket?.id) {
      const mnpData: MnpData & { portingWishdate?: string } = {
        portedPhoneNumber: { phone: data.currentNumber },
        currentProvider: {
          id: providerId,
          description: data.currentProvider,
        },
        mnpContractType:
          data.mnpContractType?.toString() ===
          mnpContractType.postpaid.id?.toString()
            ? mnpContractType.postpaid
            : mnpContractType.prepaid,
        mnpTime:
          data.mnpTime?.toString() === mnpTime.after.id.toString()
            ? mnpTime.after
            : mnpTime.before,
        msisdn: data.currentNumber,
      };

      // Include wish date if applicable.
      if (
        wishdateSelectable &&
        data.mnpTime?.toString() === mnpTime.before.id.toString()
      ) {
        mnpData.portingWishdate = data.mnpWishDate;
      }

      setMnpData(
        { basketId: dataContext.basket.id, index: 0, data: mnpData },
        {
          onSuccess: (basket) => {
            setDataContext((prev) => ({
              ...prev,
              basket,
              numberPorting: data,
              allowedMaxOnboardingStep: 4,
            }));
            navigateToNextStep();
          },
        },
      );
    } else {
      // Error logging for unexpected navigation state.
      log(`
      dataContext.basket?.id: ${dataContext.basket?.id ? dataContext.basket?.id : "missing"}
      formData ${data ? data : "missing"}
      formData.currentNumber ${data.currentNumber ? data.currentNumber : "missing"}
      formData.currentProvider ${data.currentProvider ? data.currentProvider : "missing"}
      formData.mnpContractType ${data.mnpContractType ? data.mnpContractType : "missing"}
      formData.mnpTime ${data.mnpTime ? data.mnpTime : "missing"}
   `);
      withScope(function (scope) {
        scope.setTag("section", "portal");
        scope.setTag("onboardingStep", "numberporting");
        scope.setContext("numberPortingForm", {
          basketId: dataContext.basket?.id,
          formErrors: methods.formState.errors,
        });
        captureMessage(
          `Navigation in NumberPorting was triggered while it should be disabled.`,
        );
      });
    }
  };

  /**
   * Submit handler for the Portal flow (completing MNP for a specific SIM).
   *
   * @param formValues - Current form values.
   * @param msisdnValidationStatus - Validation status of the MSISDN.
   */
  const submitNumberPorting = (
    formValues: NumberPortingFormProps,
    msisdnValidationStatus: string,
  ) => {
    const parsedSimId = Number(simId);
    if (!Number.isFinite(parsedSimId)) {
      showAlert({
        text: t("translation:Common.generalForm.error.genericError"),
        type: AlertTypes.error,
      });

      return;
    }

    const abortONP = formValues.enableNumberPorting === "no";

    // If aborting, just send the abort flag.
    if (abortONP) {
      completeMnpData({
        simId: parsedSimId,
        data: { abortONP: true },
      });

      return;
    }

    // Only proceed if the number is valid.
    if (msisdnValidationStatus !== MSISDN_VALIDATION_STATUS.VALID) {
      return;
    }

    const providerId = providers?.[formValues.currentProvider];
    if (!providerId) {
      setError("currentProvider", {
        type: "backendError",
        message: t("Common.generalForm.error.genericError"),
      });

      return;
    }

    // Prepare the payload for completion API.
    const mnpData: CompleteMnpData = {
      mnpData: {
        portedPhoneNumber: { phone: formValues.currentNumber },
        currentProvider: {
          id: providerId,
          description: formValues.currentProvider,
        },
        mnpContractType:
          formValues.mnpContractType?.toString() ===
          mnpContractType.postpaid.id?.toString()
            ? mnpContractType.postpaid
            : mnpContractType.prepaid,
        mnpTime:
          formValues.mnpTime?.toString() === mnpTime.after.id.toString()
            ? mnpTime.after
            : mnpTime.before,
        msisdn: formValues.currentNumber,
        ...(wishdateSelectable &&
        formValues.mnpTime?.toString() === mnpTime.before.id.toString()
          ? { portingWishdate: formValues.mnpWishDate }
          : {}),
      },
      abortONP: abortONP,
    };

    completeMnpData({
      simId: parsedSimId,
      data: mnpData,
    });
  };

  return {
    onSubmit,
    submitNumberPorting,
    isSettingMnpData,
    isClearingOnp,
    isCompletingMnpData,
  };
};
